import { useCallback, useEffect, useState } from 'react'
import { DEFAULT_HOUR, loadReminder, saveReminder } from '../../platform/api'
import { isInTelegram, requestWriteAccess } from '../../platform/telegram'
import { useT } from '../../platform/i18n/useT'
import './screens.css'

/**
 * Настройка напоминаний.
 *
 * Показывается только внутри Telegram: вне его ни разрешения
 * запросить, ни сообщение отправить нельзя, и переключатель,
 * который ничего не делает, хуже отсутствующего.
 *
 * Разрешение запрашивается ровно в момент включения, а не заранее.
 * Спрашивать до того, как человек выразил желание, - верный способ
 * получить отказ.
 */

const HOURS = [8, 12, 15, 19, 21] as const

export function Reminders() {
  const { t, lang } = useT()
  const [enabled, setEnabled] = useState(false)
  const [hour, setHour] = useState<number>(DEFAULT_HOUR)
  const [busy, setBusy] = useState(false)
  const [denied, setDenied] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let alive = true
    void (async () => {
      const current = await loadReminder()
      if (!alive) return
      if (current) {
        setEnabled(current.enabled)
        setHour(current.hour)
      }
      setReady(true)
    })()
    return () => {
      alive = false
    }
  }, [])

  const toggle = useCallback(async () => {
    if (busy) return
    setBusy(true)
    setDenied(false)

    try {
      if (!enabled) {
        const granted = await requestWriteAccess()
        if (!granted) {
          setDenied(true)
          return
        }
      }
      const next = !enabled
      const ok = await saveReminder(hour, next, lang)
      if (ok) setEnabled(next)
    } finally {
      setBusy(false)
    }
  }, [busy, enabled, hour, lang])

  const pickHour = useCallback(
    async (next: number) => {
      setHour(next)
      if (enabled) await saveReminder(next, true, lang)
    },
    [enabled, lang],
  )

  if (!isInTelegram() || !ready) return null

  return (
    <>
      <div className="sc__label">{t('reminder.title')}</div>

      <div className="sc__kv">
        <span>{t('reminder.daily')}</span>
        <button
          type="button"
          className={enabled ? 'sc__switch sc__switch--on' : 'sc__switch'}
          onClick={() => void toggle()}
          disabled={busy}
          aria-pressed={enabled}
        >
          <i />
        </button>
      </div>

      {enabled && (
        <div className="sc__kv">
          <span>{t('reminder.time')}</span>
          <div className="sc__seg">
            {HOURS.map((h) => (
              <button
                key={h}
                className={h === hour ? 'on' : ''}
                onClick={() => void pickHour(h)}
              >
                {h}:00
              </button>
            ))}
          </div>
        </div>
      )}

      {denied && <p className="sc__empty">{t('reminder.denied')}</p>}
      {!denied && !enabled && <p className="sc__empty">{t('reminder.hint')}</p>}
    </>
  )
}
